import type { ComponentProps } from 'react';
import React from 'react';

import type { Collaborators } from '@atlassian/cross-join-nudges';
import {
	RequestAccessErrorFlag,
	RequestAccessPromptFlag,
	RequestAccessSuccessFlag,
	requestAccess,
} from '@atlassian/cross-join-nudges';

import type { FlagsStateContainer } from '@confluence/flags';
import { useChoreographerAssets } from '@confluence/choreographer-services';

import {
	getFeatureVisitedCount,
	increaseFeatureVisitedCount,
	setUserOptedOut,
	snoozeFeatureFor30Days,
} from '../services/feature-visited';
import {
	fireFlagDismissed,
	fireFlagShown,
	fireFlagSnoozed,
	fireJoinButtonClicked,
	fireUserOptedOut,
	getExperimentAnalytics,
} from '../services/analytics';
import type { CrossJoinNudgesInput } from '../services/run-cross-join-nudges';

const PRODUCT = 'jira-software';
const MAX_NUMBER_OF_COLLABORATORS = 5;

type ShowNudgeFlagInput = Pick<
	CrossJoinNudgesInput,
	'cloudId' | 'flags' | 'createAnalyticsEvent'
> & {
	userId: string;
	collaborators: Collaborators;
	pushRoute: CrossJoinNudgesInput['push'];
};

export const showNudgeFlag = ({
	userId,
	cloudId,
	collaborators,
	createAnalyticsEvent,
	flags,
	pushRoute,
}: ShowNudgeFlagInput) => {
	const featureVisitedCount = getFeatureVisitedCount();
	const attributes = {
		collaborators: collaborators.length,
		userId,
		featureVisitedCount,
	};
	const analytics = getExperimentAnalytics(createAnalyticsEvent, {
		attributes,
	});

	const dismiss = (id: string | number, flags: FlagsStateContainer) => {
		fireFlagDismissed(analytics);
		// TOFIX: typescript-eslint/no-floating-promises
		// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
		// eslint-disable-next-line
		flags.hideFlag(id);
	};

	const dismissAndSnooze = (id: string | number, flags: FlagsStateContainer) => {
		dismiss(id, flags);
		fireFlagSnoozed(analytics);
		snoozeFeatureFor30Days();
	};

	const dismissAndOptout = (id: string | number, flags: FlagsStateContainer) => {
		dismiss(id, flags);
		fireUserOptedOut(analytics);
		setUserOptedOut();
	};

	// TOFIX: typescript-eslint/no-floating-promises
	// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
	// eslint-disable-next-line
	flags.showFlag({
		customComponent: (props) => (
			<ChoreographerRequestAccessPromptFlag
				id={props.id}
				collaborators={collaborators.slice(0, MAX_NUMBER_OF_COLLABORATORS)}
				targetProduct={PRODUCT}
				viewCount={featureVisitedCount}
				incrementViewCount={increaseFeatureVisitedCount}
				onDismiss={() => dismiss(props.id, flags)}
				onSnoozeAndDismiss={() => dismissAndSnooze(props.id, flags)}
				onOptoutAndDismiss={() => dismissAndOptout(props.id, flags)}
				onSubmit={async () => {
					try {
						const accessStatus = await requestAccess(userId, cloudId, createAnalyticsEvent);
						if (
							accessStatus === 'ACCESS_GRANTED' ||
							accessStatus === 'ACCESS_EXISTS' ||
							accessStatus === 'APPROVED_REQUEST_EXISTS'
						) {
							pushRoute('/jira');
						} else if (
							accessStatus === 'PENDING_REQUEST_CREATED' ||
							accessStatus === 'PENDING_REQUEST_EXISTS'
						) {
							// TOFIX: typescript-eslint/no-floating-promises
							// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
							// eslint-disable-next-line
							flags.hideFlag(props.id);
							showSuccessFlag(flags);
						} else {
							// TOFIX: typescript-eslint/no-floating-promises
							// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
							// eslint-disable-next-line
							flags.hideFlag(props.id);
							showErrorFlag(flags);
						}
					} catch {
						// TOFIX: typescript-eslint/no-floating-promises
						// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
						// eslint-disable-next-line
						flags.hideFlag(props.id);
						showErrorFlag(flags);
					}
					fireJoinButtonClicked(analytics);
				}}
			/>
		),
	});

	fireFlagShown(analytics);
};

const showSuccessFlag = (flags: FlagsStateContainer) => {
	// TOFIX: typescript-eslint/no-floating-promises
	// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
	// eslint-disable-next-line
	flags.showFlag({
		customComponent: (props) => (
			<RequestAccessSuccessFlag id={props.id} onDismiss={() => flags.hideFlag(props.id)} />
		),
	});
};

const showErrorFlag = (flags: FlagsStateContainer) => {
	// TOFIX: typescript-eslint/no-floating-promises
	// Ticket: https://atl-growth.atlassian.net/browse/VD-1437
	// eslint-disable-next-line
	flags.showFlag({
		customComponent: (props) => (
			<RequestAccessErrorFlag
				id={props.id}
				targetProduct={PRODUCT}
				onDismiss={() => flags.hideFlag(props.id)}
			/>
		),
	});
};

// Choreographer Wrapper around RequestAccessPromptFlag
function ChoreographerRequestAccessPromptFlag({
	...passThrough
}: ComponentProps<typeof RequestAccessPromptFlag>) {
	const { useChoreographedRender } = useChoreographerAssets();

	const canShow = useChoreographedRender({
		messageId: 'cc-cross-join-nudge',
	});

	return canShow ? <RequestAccessPromptFlag {...passThrough} /> : null;
}
